import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoSharpIcon from '@mui/icons-material/InfoSharp';
import WarningSharpIcon from '@mui/icons-material/WarningSharp';
import { Alert, IconButton, Snackbar, SnackbarContent } from '@mui/material';
import * as React from 'react';
import { getErrorMessage } from '../utils';
/**
 * Displays a general informational snackbar message.
 *
 * @param {SnackbarParams} params - Parameters to configure the snackbar.
 * @returns {void}
 */
export function showInfoSnackbar(params) {
    if (instance) {
        instance.setState((state) => {
            var _a, _b;
            return ({
                queue: [
                    ...state.queue,
                    {
                        message: params.message,
                        autoHideDuration: params.autoHideDuration,
                        alertType: undefined,
                        verticalPosition: (_a = params.verticalPosition) !== null && _a !== void 0 ? _a : 'bottom',
                        horizontalPosition: (_b = params.horizontalPosition) !== null && _b !== void 0 ? _b : 'center',
                    },
                ],
            });
        });
    }
}
/**
 * Displays an alert-style snackbar with a specific severity (error, warning, info, success).
 *
 * @param {AlertSnackbarParams} params - Parameters including type and snackbar configuration.
 * @returns {void}
 */
export function showAlertSnackbar(params) {
    if (instance) {
        instance.setState((state) => {
            var _a, _b;
            return ({
                queue: [
                    ...state.queue,
                    {
                        message: params.message,
                        autoHideDuration: params.autoHideDuration,
                        alertType: params.type,
                        verticalPosition: (_a = params.verticalPosition) !== null && _a !== void 0 ? _a : 'bottom',
                        horizontalPosition: (_b = params.horizontalPosition) !== null && _b !== void 0 ? _b : 'center',
                    },
                ],
            });
        });
    }
}
/**
 * Logs the error to the console and displays an error snackbar with a derived message.
 *
 * @param {unknown} error - The error to handle and display.
 * @returns {void}
 */
export function handleErrorSnackbar(error) {
    console.error(error);
    showAlertSnackbar({ message: getErrorMessage(error), type: 'error' });
}
const alertIcon = {
    error: ErrorOutlineIcon,
    warning: WarningSharpIcon,
    info: InfoSharpIcon,
    success: CheckCircleOutlineIcon,
};
let instance;
/**
 * Singleton React component that manages a global queue of snackbars or alerts.
 * It ensures that only one SnackbarHelper instance exists at any time.
 * Displays snackbar messages sequentially with optional auto-hide and alert severity styling.
 *
 * @template unknown - Props are not used in this component.
 * @augments {React.Component<unknown, State>}
 */
export class SnackbarHelper extends React.Component {
    /**
     * @param {unknown} props - Component props (not used).
     */
    constructor(props) {
        super(props);
        this.handleClose = (object) => {
            this.setState((state) => ({
                queue: state.queue.filter((n) => n !== object),
                pause: true,
            }), () => setTimeout(() => this.setState({ pause: false }), 200));
        };
        this.state = {
            queue: [],
            pause: false,
        };
    }
    componentDidMount() {
        if (!instance) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            instance = this;
        }
        else {
            console.error('SnackbarHelper should be used only once');
        }
    }
    componentWillUnmount() {
        if (instance === this) {
            instance = undefined;
        }
    }
    /**
     * @returns {React.ReactNode} A Snackbar or Alert component if a message exists in the queue; otherwise null.
     */
    render() {
        const object = !this.state.pause && this.state.queue.length > 0 ? this.state.queue[0] : undefined;
        if (object) {
            let dataTest = 'info-snackbar';
            if (object.alertType) {
                dataTest = 'alert-' + object.alertType + '-snackbar';
            }
            return (React.createElement(Snackbar, { open: true, anchorOrigin: {
                    vertical: object.verticalPosition,
                    horizontal: object.horizontalPosition,
                }, autoHideDuration: object.autoHideDuration || 10000, onClose: (event, reason) => {
                    if (reason !== 'clickaway') {
                        this.handleClose(object);
                    }
                } }, object.alertType ? (React.createElement(Alert, { elevation: 6, severity: object.alertType, "data-test": dataTest, onClose: () => this.handleClose(object), sx: { width: '100%' } }, object.message)) : (React.createElement(SnackbarContent, { message: React.createElement("span", { style: { display: 'flex', alignItems: 'center' }, "data-test": dataTest },
                    React.createElement("span", null, object.message)), action: React.createElement(IconButton, { color: 'inherit', onClick: () => this.handleClose(object), "data-test": `${dataTest}-button`, size: 'large' },
                    React.createElement(CloseIcon, { sx: { fontSize: 20 } })) }))));
        }
        else {
            return null;
        }
    }
}
